/* ===================================================================
   useCompetitors() + useCompetitorMutations() -- tracked competitors
   (Brad, 2026-06-17).

   Read:   GET    /api/v1/competitors  (latest snapshot per competitor)
   Add:    POST   /api/v1/competitors
   Remove: DELETE /api/v1/competitors/{place_id}

   The Competitors screen binds to these. metrics default to 0 until a
   snapshot job populates them (no external API at write time). After a
   write we invalidate so the list refetches. Read hook disabled until a
   Bearer rr_ token exists.
   =================================================================== */
import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import {
  api,
  type Competitor,
  type CompetitorInput,
  type CompetitorsRefreshResult,
  type CompetitorsResponse,
  type CompetitorScorecardResponse,
  type PlacesSearchResponse,
} from '../lib/api';
import { useApiToken } from './useApiToken';
import { qk } from './queryKeys';

/* Business-name PICKER search (GET /places/search). The caller passes an
   already-debounced query (Places bills per call) + optional city. Disabled
   until there is a token AND a non-empty query, so no idle/empty calls fire.
   A 200 results:[] is a valid "no matches" state, not an error. */
export function usePlacesSearch(
  q: string,
  near: string,
): UseQueryResult<PlacesSearchResponse, Error> {
  const hasToken = Boolean(useApiToken());
  const query = q.trim();
  const city = near.trim();
  return useQuery({
    queryKey: qk.placesSearch(query, city),
    queryFn: ({ signal }) => api.searchPlaces(query, city, signal),
    enabled: hasToken && query.length > 0,
    staleTime: 5 * 60_000,
    retry: false,
  });
}

export function useCompetitors(): UseQueryResult<CompetitorsResponse, Error> {
  const hasToken = Boolean(useApiToken());
  return useQuery({
    queryKey: qk.competitors(),
    queryFn: ({ signal }) => api.listCompetitors(signal),
    enabled: hasToken,
    staleTime: 60_000,
  });
}

/* Competitor scorecard (derived metrics + leaderboard) from stored snapshots.
   Disabled until a Bearer rr_ token exists. The response always carries a
   leaderboard (the client is inserted even with zero competitors), so the view
   has something honest to show on day one. */
export function useCompetitorScorecard(): UseQueryResult<
  CompetitorScorecardResponse,
  Error
> {
  const hasToken = Boolean(useApiToken());
  return useQuery({
    queryKey: qk.competitorScorecard(),
    queryFn: ({ signal }) => api.getCompetitorScorecard(signal),
    enabled: hasToken,
    staleTime: 60_000,
  });
}

export interface UseCompetitorMutationsResult {
  add: (input: CompetitorInput) => Promise<Competitor>;
  remove: (placeId: string) => Promise<string>;
  refresh: () => Promise<CompetitorsRefreshResult>;
  isPending: boolean;
  isRefreshing: boolean;
  error: Error | null;
}

export function useCompetitorMutations(): UseCompetitorMutationsResult {
  const qc = useQueryClient();
  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: qk.competitors() });
  };
  const addM = useMutation({
    mutationFn: (input: CompetitorInput) => api.createCompetitor(input),
    onSuccess: invalidate,
  });
  const removeM = useMutation({
    mutationFn: (placeId: string) => api.deleteCompetitor(placeId),
    onSuccess: invalidate,
  });
  const refreshM = useMutation({
    mutationFn: () => api.refreshCompetitors(),
    onSuccess: invalidate,
  });
  return {
    add: (input) => addM.mutateAsync(input).then((r) => r.competitor),
    remove: (placeId) => removeM.mutateAsync(placeId).then((r) => r.deleted),
    refresh: () => refreshM.mutateAsync(),
    isPending: addM.isPending || removeM.isPending,
    isRefreshing: refreshM.isPending,
    error: addM.error ?? removeM.error ?? refreshM.error ?? null,
  };
}
